import {Heading, Integer} from 'platform/foundation';
import styled, {useTheme} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {clamp, isNil} from 'ramda';
import {floor} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIsPrintForced} from '../../../hooks/useIsPrintForced';
import {ColorSegmentColor, NeutralColorSegment} from '../../../types/ColorSegmentColor';

interface AttractivenessMarkerProps extends TestIdProps {
  value?: number;
  segments: unknown[];
  chartWidth: number;
}

export function AttractivenessMarker(props: AttractivenessMarkerProps) {
  const theme = useTheme();
  const {getResponsiveValue} = useIsPrintForced();
  const position = isNil(props.value)
    ? 0.5 * props.chartWidth
    : (props.value / 100) * props.chartWidth;
  const markerWidthInSteps = getResponsiveValue(18, 18, 28, 28, 28);
  const whiteCircleWidthInSteps = getResponsiveValue(12, 12, 20, 20, 20);
  const markerWidth = markerWidthInSteps / theme.space.step;
  const overflowsLeft = position < markerWidth / 2;
  const overflowsRight = position > props.chartWidth - markerWidth / 2;
  const segmentCount = props.segments.length;
  const segmentWidth = props.chartWidth / segmentCount;
  const startSegmentIndex = clamp(
    0,
    segmentCount - 1,
    floor((position - markerWidth / 2) / segmentWidth)
  );
  const endSegmentIndex = clamp(
    startSegmentIndex,
    segmentCount - 1,
    floor((position + markerWidth / 2) / segmentWidth)
  );

  return (
    <ValueMarker
      $position={props.value ?? 50}
      $size={markerWidthInSteps}
      $overflowsLeft={overflowsLeft}
      $overflowsRight={overflowsRight}
      $startColor={getSegmentColor(startSegmentIndex, isNil(props.value))}
      $endColor={getSegmentColor(endSegmentIndex, isNil(props.value))}
      data-testid={suffixTestId('attractivenessMarker', props)}
    >
      <WhiteCircle $size={whiteCircleWidthInSteps}>
        <Heading
          size={getResponsiveValue(5, 5, 3, 3, 3)}
          data-testid={suffixTestId('attractivenessValue', props)}
        >
          {isNil(props.value) ? 'N/A' : `${floor(props.value)}%`}
        </Heading>
      </WhiteCircle>
    </ValueMarker>
  );
}

const getSegmentColor = (index: number, isUnknown: boolean) =>
  match([index, isUnknown])
    .with([0, false], () => ColorSegmentColor.VeryLow)
    .with([1, false], () => ColorSegmentColor.Low)
    .with([2, false], () => ColorSegmentColor.Medium)
    .with([3, false], () => ColorSegmentColor.High)
    .with([4, false], () => ColorSegmentColor.VeryHigh)
    .with([Pattern.any, true], () => NeutralColorSegment.Center)
    .otherwise(() => {
      throw new Error('Invalid segment index');
    });

interface ValueMarkerProps {
  $position: number;
  $size: Integer;
  $overflowsLeft: boolean;
  $overflowsRight: boolean;
  $startColor: string;
  $endColor: string;
}

const ValueMarker = styled.div<ValueMarkerProps>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  left: ${({$position: position, $overflowsLeft, $overflowsRight}) =>
    match({left: $overflowsLeft, right: $overflowsRight})
      .with({left: true}, () => '0')
      .with({right: true}, () => '100')
      .otherwise(() => position)}%;
  transform: translate(
    ${({$overflowsLeft, $overflowsRight}) =>
      match({left: $overflowsLeft, right: $overflowsRight})
        .with({left: true}, () => '0%')
        .with({right: true}, () => '-100%')
        .otherwise(() => '-50%')},
    -50%
  );
  width: ${({theme, $size}) => theme.getSize($size)};
  height: ${({theme, $size}) => theme.getSize($size)};
  background: ${({$startColor: startColor, $endColor: endColor}) =>
    `linear-gradient(to right, ${startColor}, ${endColor})`};
  border-radius: 50%;
`;

interface WhiteCircleProps {
  $size: Integer;
}

const WhiteCircle = styled.div<WhiteCircleProps>`
  width: ${({theme, $size}) => theme.getSize($size)};
  height: ${({theme, $size}) => theme.getSize($size)};
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 0 ${({theme}) => theme.getSize(7)} rgba(0, 0, 0, 0.1);
`;
